import { publicApi } from '../api/api';

/**
 * Fallback Payment Logger
 * Logs fallback payment modal events to backend for analytics
 */

// Browser detection helper
const getBrowserInfo = () => {
  const ua = navigator.userAgent;
  
  let browserName = 'unknown';
  if (ua.includes('Chrome') && !ua.includes('Edg')) browserName = 'Chrome';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browserName = 'Safari';
  else if (ua.includes('Firefox')) browserName = 'Firefox';
  else if (ua.includes('Edg')) browserName = 'Edge';
  else if (ua.includes('Opera') || ua.includes('OPR')) browserName = 'Opera';
  
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
  
  // Detect in-app browsers
  const isInAppBrowser = /FBAN|FBAV|Instagram|Twitter|Line|Snapchat|WhatsApp|TikTok|Telegram/i.test(ua);
  
  return {
    browserName,
    isMobile,
    isInAppBrowser,
    userAgent: ua.substring(0, 500) // Truncate for storage
  };
};

/**
 * Log a fallback payment event
 * @param {Object} params - Event parameters
 * @returns {Promise<void>}
 */
export const logFallbackEvent = async ({
  orderId,
  leadId = null,
  userId = null,
  eventType,
  amount = null,
  currency = 'INR',
  qrCodeId = null,
  paymentLinkId = null,
  paymentLinkUrl = null,
  triggerReason = 'unknown',
  errorInfo = null,
  orderStatusAtEvent = null,
  metadata = null
}) => {
  // Fire and forget - don't await or block UI
  try {
    const browserInfo = getBrowserInfo();
    
    publicApi.post('/payment/fallback-log', {
      orderId,
      leadId,
      userId,
      eventType,
      amount,
      currency,
      qrCodeId,
      paymentLinkId,
      paymentLinkUrl,
      triggerReason,
      browserInfo,
      errorInfo,
      orderStatusAtEvent,
      metadata
    }).catch(err => {
      // Silent failure - logging should never break the flow
      console.warn('Failed to log fallback event:', err.message);
    });
  } catch (error) {
    console.warn('Failed to log fallback event:', error.message);
  }
};

// Convenience methods for common events
export const FallbackPaymentLogger = {
  /**
   * Log modal opened
   */
  modalOpened: (orderId, amount, triggerReason = 'unknown', metadata = {}) => {
    logFallbackEvent({
      orderId,
      amount,
      eventType: 'modal_opened',
      triggerReason,
      errorInfo: metadata.errorInfo || null,
      metadata: {
        browserInfo: metadata.browserInfo,
        ...metadata
      }
    });
  },

  /**
   * Log status check (before generating QR)
   */
  statusCheck: (orderId) => {
    logFallbackEvent({
      orderId,
      eventType: 'status_check'
    });
  },

  /**
   * Log already paid - QR skipped
   */
  alreadyPaidSkipped: (orderId, orderStatus) => {
    logFallbackEvent({
      orderId,
      eventType: 'already_paid_skipped',
      orderStatusAtEvent: orderStatus,
      metadata: { skippedQrGeneration: true }
    });
  },

  /**
   * Log QR generated successfully
   */
  qrGenerated: (orderId, amount, qrCodeId, paymentLinkId = null, paymentLinkUrl = null) => {
    logFallbackEvent({
      orderId,
      amount,
      eventType: 'qr_generated',
      qrCodeId,
      paymentLinkId,
      paymentLinkUrl
    });
  },

  /**
   * Log QR generation failed
   */
  qrGenerationFailed: (orderId, amount, errorInfo) => {
    logFallbackEvent({
      orderId,
      amount,
      eventType: 'qr_generation_failed',
      errorInfo
    });
  },

  /**
   * Log payment link generated (fallback when QR fails)
   */
  paymentLinkFallback: (orderId, amount, paymentLinkId, paymentLinkUrl) => {
    logFallbackEvent({
      orderId,
      amount,
      eventType: 'payment_link_fallback',
      paymentLinkId,
      paymentLinkUrl
    });
  },

  /**
   * Log payment completed via polling
   */
  paymentCompleted: (orderId, amount, paymentData = {}) => {
    logFallbackEvent({
      orderId,
      amount,
      eventType: 'payment_completed',
      metadata: paymentData
    });
  },

  /**
   * Log modal closed by user
   */
  modalClosed: (orderId, wasPaymentCompleted = false) => {
    logFallbackEvent({
      orderId,
      eventType: 'modal_closed',
      metadata: { wasPaymentCompleted }
    });
  },

  /**
   * Log retry clicked
   */
  retryClicked: (orderId) => {
    logFallbackEvent({
      orderId,
      eventType: 'retry_clicked'
    });
  },

  /**
   * Log any error
   */
  error: (orderId, errorInfo, context = {}) => {
    logFallbackEvent({
      orderId,
      eventType: 'error',
      errorInfo,
      metadata: context
    });
  }
};

export default FallbackPaymentLogger;
